// AUTH CONTEXT
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import {
  User as firebaseUser,
  signInWithEmailAndPassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { User as UserData } from '@prisma/client';

interface AuthContextType {
  isLoggedIn: boolean;
  login: ({ credentials }: { credentials: Credentials }) => Promise<UserData | null>;
  logout: () => Promise<boolean>;
  user: firebaseUser | null;
  userData: UserData | null;
  registerAccount: ({ registerData }: { registerData: RegisterData }) => Promise<UserData | null>;
  deleteAccount: ({ password }: { password: string }) => void;
}
export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}
export interface Credentials {
  email: string;
  password: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<firebaseUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setIsLoggedIn(true);
        setUser(user);
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
    });
    return unsubscribe;
  }, []);

  const getUserData = useCallback(
    async (token?: string) => {
      try {
        const bearer = token ? token : await user?.getIdToken();

        const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/auth/user', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${bearer}`,
          },
        });
        const data = await response.json();
        return data.user;
      } catch (error) {
        console.error('Error fetching user data', error);
      }
    },
    [user]
  );
  useEffect(() => {
    if (user && !userData) {
      getUserData().then((data) => {
        if (data) setUserData(data);
      });
    }
  }, [user, userData, getUserData]);

  const login = async ({ credentials }: { credentials: Credentials }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const userData = await getUserData(await userCredential.user.getIdToken());
      const user = userCredential.user;
      setUser(user);
      setIsLoggedIn(true);
      setUserData(userData);
      if (user && userData) {
        return userData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error signing in', error);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);

      setIsLoggedIn(false);
      setUser(null);
      setUserData(null);
      return true;
    } catch (error) {
      console.error('Error logging out', error);
      return false;
    }
  };

  const registerAccount = async ({ registerData }: { registerData: RegisterData }) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, registerData.email, registerData.password);
      const backendResponse = await sendRegisterData(registerData, userCredential.user);
      if (userCredential.user && backendResponse.newUser) {
        const userData = await getUserData(await userCredential.user.getIdToken());
        setUser(userCredential.user);
        setIsLoggedIn(true);
        setUserData(userData);
        if (userData) {
          return userData;
        } else {
          return null;
        }
      }
    } catch (error) {
      console.error('Error signing in', error);
    }
  };
  const deleteAccount = async ({ password }: { password: string }) => {
    if (!user) return;
    const token = await user?.getIdToken();

    try {
      // Luo tunnistetiedot (AuthCredential)
      const credential = EmailAuthProvider.credential(user.email!, password);

      // Uudelleenautentikoi käyttäjä
      await reauthenticateWithCredential(user, credential);

      // Lähetä pyyntö palvelimelle
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.deleted) {
        // Poista käyttäjä Firebase Auth:sta
        await user.delete();
        setIsLoggedIn(false);
        setUser(null);
        setUserData(null);
        window.location.href = '/register';
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Invalid password or unable to reauthenticate. Please try again.');
    }
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout, user, userData, registerAccount, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext) as AuthContextType;

async function sendRegisterData(registerData: RegisterData, user: firebaseUser) {
  const randomColor = Math.floor(Math.random() * 16777215).toString(16);
  const token = await user.getIdToken();
  const userData = {
    firstName: registerData.firstName,
    lastName: registerData.lastName,
    email: registerData.email,
    color: randomColor,
    uid: user.uid,
  };
  try {
    const response = await fetch(process.env.REACT_APP_BACKEND_URL + '/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userData }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error registering user', error);
  }
}
